/* Lire un fichier que quelqu'un a tapé à la main, et dire ce qui ne va pas.
 *
 * Un import rate rarement sur le fond : il rate sur une colonne mal nommée, un
 * prix écrit « 1,34 € / kg », une usine orthographiée de travers. Ce module ne
 * touche ni au réseau ni à React — il prend un tableau de cellules, un modèle,
 * et rend deux choses : les lignes propres, et la liste exacte de ce qui
 * cloche, ligne par ligne, colonne par colonne. C'est ce qui permet de montrer
 * l'aperçu avant d'écrire quoi que ce soit en base.
 */

export type Cellule = string | number | boolean | null | undefined;
export type Rangee = readonly Cellule[];

export type TypeChamp = "texte" | "nombre" | "entier" | "booleen" | "liste";

export interface Champ {
  cle: string;
  /** En-tête attendu dans le fichier. */
  titre: string;
  /** Autres en-têtes acceptés — on ne fait pas recommencer pour un synonyme. */
  alias?: readonly string[];
  type: TypeChamp;
  requis?: boolean;
  /** Dictionnaire à consulter : la valeur doit y exister, et devient son identifiant. */
  reference?: string;
  /** Pour un champ « liste » : les valeurs admises. */
  valeurs?: readonly string[];
  /** Ce que vaut la cellule laissée vide. */
  defaut?: string | number | boolean | null;
  aide?: string;
}

export interface Modele {
  cle: string;
  titre: string;
  /** Une phrase : ce que ce fichier pose en base. */
  propos: string;
  champs: readonly Champ[];
}

export interface ErreurImport {
  /** Numéro de ligne tel qu'il apparaît dans le tableur, en-tête comprise. */
  ligne: number;
  colonne?: string;
  message: string;
}

export type Valeur = string | number | boolean | null;

export interface LigneImport {
  ligne: number;
  valeurs: Record<string, Valeur>;
}

export interface Rapport {
  entetes: string[];
  /** Colonnes du fichier qui ne correspondent à rien — simplement ignorées. */
  inconnues: string[];
  /** Colonnes obligatoires absentes du fichier : rien ne peut être importé. */
  manquantes: string[];
  lignes: LigneImport[];
  erreurs: ErreurImport[];
  /** Lignes lues, en-tête exclue. */
  lues: number;
}

/** Dictionnaires de résolution : clé normalisée → identifiant. */
export type Dictionnaires = Readonly<Record<string, ReadonlyMap<string, string>>>;

/* ─────────────────────────────── normalisation ──────────────────────────── */

/** Sans accents, sans casse, sans espaces en trop : de quoi comparer deux mots. */
export function normalise(v: unknown): string {
  return String(v ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[ \s]+/g, " ")
    .replace(/[^a-z0-9 ]/g, "")
    .trim();
}

export function texte(v: Cellule): string {
  return String(v ?? "").replace(/[ \s]+/g, " ").trim();
}

/**
 * Un nombre écrit par un humain.
 *
 * On accepte le point comme la virgule, les espaces de milliers, et tout ce
 * qui traîne autour (« 1,34 € / kg », « 12 % »). Quand les deux séparateurs
 * sont là, le dernier est le décimal : « 1.234,56 » et « 1,234.56 » valent
 * tous deux 1234,56. Une virgule seule est décimale — on est en France.
 */
export function nombre(v: Cellule): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const brut = texte(v);
  if (!brut) return null;
  const garde = brut.replace(/[^0-9.,\-]/g, "");
  if (!garde || !/[0-9]/.test(garde)) return null;

  const dernierPoint = garde.lastIndexOf(".");
  const derniereVirgule = garde.lastIndexOf(",");
  let net: string;
  if (dernierPoint >= 0 && derniereVirgule >= 0) {
    const sep = Math.max(dernierPoint, derniereVirgule);
    net = garde.slice(0, sep).replace(/[.,]/g, "") + "." + garde.slice(sep + 1);
  } else if (derniereVirgule >= 0) {
    net = garde.replace(",", ".");
  } else {
    net = garde;
  }
  net = net.replace(/(?!^)-/g, "");
  const n = Number(net);
  return Number.isFinite(n) ? n : null;
}

const VRAI = new Set(["oui", "o", "vrai", "true", "1", "x", "yes", "y", "actif"]);
const FAUX = new Set(["non", "n", "faux", "false", "0", "", "inactif"]);

export function booleen(v: Cellule): boolean | null {
  if (typeof v === "boolean") return v;
  const n = normalise(v);
  if (VRAI.has(n)) return true;
  if (FAUX.has(n)) return false;
  return null;
}

/* ──────────────────────────────── colonnes ──────────────────────────────── */

interface Appariement {
  colonnes: Map<string, number>;
  inconnues: string[];
  manquantes: string[];
}

/** Associe chaque champ à sa colonne, par en-tête ou par alias. */
export function apparie(entetes: readonly string[], champs: readonly Champ[]): Appariement {
  const vus = entetes.map(normalise);
  const colonnes = new Map<string, number>();
  const pris = new Set<number>();

  for (const c of champs) {
    const cibles = [c.titre, c.cle, ...(c.alias ?? [])].map(normalise);
    const i = vus.findIndex((e, idx) => !pris.has(idx) && e.length > 0 && cibles.includes(e));
    if (i >= 0) {
      colonnes.set(c.cle, i);
      pris.add(i);
    }
  }

  const inconnues = entetes.filter((e, i) => !pris.has(i) && texte(e).length > 0);
  const manquantes = champs
    .filter((c) => c.requis && !colonnes.has(c.cle))
    .map((c) => c.titre);

  return { colonnes, inconnues, manquantes };
}

/* ───────────────────────────────── lecture ──────────────────────────────── */

/** Combien de rangées on fouille avant d'abandonner la recherche d'un en-tête. */
const PROFONDEUR_ENTETE = 25;

/**
 * Où commence le tableau.
 *
 * Pas « la première rangée non vide » : un export de tableur porte souvent un
 * titre, une date, une ligne blanche avant les vraies colonnes. On prend donc
 * la rangée qui reconnaît le plus de colonnes du modèle — et, à défaut de
 * reconnaître quoi que ce soit, la première rangée non vide, pour que le
 * rapport puisse au moins dire ce qui manque.
 */
function entete(rangees: readonly Rangee[], champs: readonly Champ[]): number {
  let meilleure = -1;
  let score = 0;
  let premiere = -1;

  const fin = Math.min(rangees.length, PROFONDEUR_ENTETE);
  for (let i = 0; i < fin; i++) {
    const r = rangees[i] ?? [];
    if (!r.some((c) => texte(c).length > 0)) continue;
    if (premiere < 0) premiere = i;
    const n = apparie(r.map(texte), champs).colonnes.size;
    if (n > score) {
      score = n;
      meilleure = i;
    }
  }

  if (score > 0) return meilleure;
  return premiere;
}

function vide(r: Rangee): boolean {
  return !r.some((c) => texte(c).length > 0);
}

/**
 * Lit un tableau de cellules selon un modèle.
 *
 * Les numéros de ligne rendus sont ceux du tableur — la ligne 2 du rapport est
 * bien la ligne 2 du fichier — pour qu'on puisse aller corriger sans compter.
 */
export function lire(
  rangees: readonly Rangee[],
  modele: Modele,
  dicos: Dictionnaires = {},
): Rapport {
  const iEntete = entete(rangees, modele.champs);
  if (iEntete < 0) {
    return { entetes: [], inconnues: [], manquantes: modele.champs.filter((c) => c.requis)
      .map((c) => c.titre), lignes: [], erreurs: [], lues: 0 };
  }

  const entetes = (rangees[iEntete] ?? []).map(texte);
  const { colonnes, inconnues, manquantes } = apparie(entetes, modele.champs);

  const lignes: LigneImport[] = [];
  const erreurs: ErreurImport[] = [];
  let lues = 0;

  for (let i = iEntete + 1; i < rangees.length; i++) {
    const r = rangees[i] ?? [];
    if (vide(r)) continue;
    lues++;
    const numero = i + 1;
    const valeurs: Record<string, Valeur> = {};
    let saine = true;

    for (const champ of modele.champs) {
      const col = colonnes.get(champ.cle);
      const brute = col === undefined ? null : (r[col] ?? null);
      const resultat = cellule(brute, champ, dicos);
      if (resultat.erreur) {
        erreurs.push({ ligne: numero, colonne: champ.titre, message: resultat.erreur });
        saine = false;
      } else {
        valeurs[champ.cle] = resultat.valeur;
      }
    }

    if (saine) lignes.push({ ligne: numero, valeurs });
  }

  return { entetes, inconnues, manquantes, lignes, erreurs, lues };
}

interface Lue {
  valeur: Valeur;
  erreur?: string;
}

function cellule(brute: Cellule, champ: Champ, dicos: Dictionnaires): Lue {
  const brut = texte(brute);

  if (!brut) {
    if (champ.requis) return { valeur: null, erreur: "obligatoire, et la case est vide" };
    return { valeur: champ.defaut ?? null };
  }

  if (champ.reference) {
    const dico = dicos[champ.reference];
    if (!dico) return { valeur: null, erreur: `référentiel « ${champ.reference} » indisponible` };
    const id = dico.get(normalise(brut));
    if (!id) return { valeur: null, erreur: `« ${brut} » inconnu` };
    return { valeur: id };
  }

  switch (champ.type) {
    case "nombre":
    case "entier": {
      const n = nombre(brute);
      if (n === null) return { valeur: null, erreur: `« ${brut} » n'est pas un nombre` };
      if (n < 0) return { valeur: null, erreur: `« ${brut} » est négatif` };
      return { valeur: champ.type === "entier" ? Math.round(n) : n };
    }
    case "booleen": {
      const b = booleen(brute);
      if (b === null) return { valeur: null, erreur: `« ${brut} » : attendu oui ou non` };
      return { valeur: b };
    }
    case "liste": {
      const admis = champ.valeurs ?? [];
      const n = normalise(brut);
      const trouve = admis.find((v) => normalise(v) === n);
      if (!trouve) {
        return { valeur: null, erreur: `« ${brut} » : attendu ${admis.join(", ")}` };
      }
      return { valeur: trouve };
    }
    default:
      return { valeur: brut };
  }
}

/* ───────────────────────────── doublons internes ────────────────────────── */

/**
 * Lignes qui se répètent à l'intérieur du fichier sur une clé donnée.
 *
 * Deux lignes pour la même référence, c'est presque toujours une erreur de
 * copier-coller — et en base, la seconde écraserait la première en silence.
 */
export function doublons(lignes: readonly LigneImport[], cles: readonly string[]): ErreurImport[] {
  const vus = new Map<string, number>();
  const sorties: ErreurImport[] = [];
  for (const l of lignes) {
    const k = cles.map((c) => normalise(l.valeurs[c])).join("\u0000");
    if (!k.replace(/\u0000/g, "")) continue;
    const premiere = vus.get(k);
    if (premiere !== undefined) {
      sorties.push({
        ligne: l.ligne,
        message: `déjà présente ligne ${premiere} dans ce fichier`,
      });
    } else vus.set(k, l.ligne);
  }
  return sorties;
}

/** Retire du lot les lignes visées par une erreur. */
export function sansLignes(
  lignes: readonly LigneImport[],
  erreurs: readonly ErreurImport[],
): LigneImport[] {
  const exclues = new Set(erreurs.map((e) => e.ligne));
  return lignes.filter((l) => !exclues.has(l.ligne));
}

/** Un index prêt pour `dicos` : plusieurs façons de désigner la même chose. */
export function dictionnaire<T>(
  elements: readonly T[],
  id: (e: T) => string,
  ...noms: ((e: T) => string | null | undefined)[]
): Map<string, string> {
  const m = new Map<string, string>();
  for (const e of elements) {
    for (const nom of noms) {
      const n = normalise(nom(e));
      if (n && !m.has(n)) m.set(n, id(e));
    }
  }
  return m;
}
